import { formatDate } from "@/lib/api";
import { CheckCircle2, Circle } from "lucide-react";

// Full milestone chain — matches the horizontal timeline the customer wants.
// Adding a step: drop an object here. Alternating direction avoids labels
// overlapping when many are visible at once.
const MILESTONES = [
  { key: "date_received",         label: "Order Received" },
  { key: "material_ordered_date", label: "Material Ordered" },
  { key: "cdr_received",          label: "CDR Received" },
  { key: "hpdf_sent",             label: "HPDF Sent" },
  { key: "hpdf_approved",         label: "HPDF Approved" },
  { key: "plates_ordered",        label: "Plates Ordered" },
  { key: "plates_received",       label: "Plates Received" },
  { key: "bpr_issue_date",        label: "BPR Issued" },
  { key: "shade_card_sent",       label: "Shade Card Sent" },
  { key: "shade_card_approved",   label: "Shade Card Approved" },
  { key: "die_ordered",           label: "Die Ordered" },
  { key: "die_received",          label: "Die Received" },
  { key: "printing_date",         label: "Printing", stageKey: "printing" },
  { key: "flatbed_date",           label: "Flatbed", stageKey: "flatbed" },
  { key: "avt_date",               label: "AVT", stageKey: "avt" },
  { key: "slitting_date",          label: "Slitting", stageKey: "slitting" },
  { key: "quality_date",           label: "Quality", stageKey: "quality" },
  { key: "packing_date",           label: "Packing", stageKey: "packing" },
  { key: "dispatch_date",          label: "Dispatch", stageKey: "dispatch" },
];

// onSelectStage(stageKey): production milestones become buttons that open their stage form.
export default function MilestonesTimeline({ job, stages = [], onSelectStage, jobDone = false }) {
  const stageByKey = Object.fromEntries(stages.map((stage) => [stage.key, stage]));
  // A stage milestone is done once either the app's own stage record says so, or — for a job already
  // dispatched or cancelled — the job itself carries that date from the source sheet (most bulk-imported
  // jobs were never walked stage-by-stage through the app, but a finished job's printing/AVT/dispatch
  // dates are real facts). Not trusted for a job still in progress: these sheet fields can be stale or
  // pre-filled placeholders (a job still "Printing" has been seen carrying an old dispatch_date), so an
  // active job's milestones stay anchored to the app's own stage completions only.
  const milestoneDate = (m) => {
    if (!m.stageKey) return job[m.key];
    const stageDate = stageByKey[m.stageKey]?.data?.date || stageByKey[m.stageKey]?.completed_at;
    return stageDate || (jobDone ? job[m.key] : null);
  };
  const doneFlags = MILESTONES.map((m) => !!milestoneDate(m));
  // Progress fill and current marker anchor at the LAST completed milestone
  // (not the count) so out-of-order completions still look sensible.
  let lastDoneIdx = -1;
  doneFlags.forEach((d, i) => { if (d) lastDoneIdx = i; });
  const activeStage = stages.find((stage) => !stage.locked && !stage.completed_at);
  const stageCurrentIdx = activeStage
    ? MILESTONES.findIndex((milestone) => milestone.stageKey === activeStage.key)
    : -1;
  const nextPendingIdx = doneFlags.findIndex((done, i) => i > lastDoneIdx && !done);
  const currentIdx = stages.length ? stageCurrentIdx : nextPendingIdx;
  const progressPct = MILESTONES.length > 1
    ? (lastDoneIdx < 0 ? 0 : (lastDoneIdx / (MILESTONES.length - 1)) * 100)
    : 0;

  return (
    <section className="border border-neutral-200 bg-white" data-testid="milestones-timeline">
      <div className="px-5 py-4 border-b border-neutral-200 label-caps flex items-center justify-between">
        <span>Milestones</span>
        <span className="text-[10px] text-neutral-500 normal-case tracking-normal">
          {doneFlags.filter(Boolean).length}/{MILESTONES.length} stages complete
        </span>
      </div>
      <div className="p-5 overflow-x-auto">
        <div className="relative min-w-[1400px] py-14">
          {/* Base rail */}
          <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-neutral-200 -translate-y-1/2" />
          {/* Filled progress rail (green up to the current stage) */}
          <div
            className="absolute top-1/2 left-0 h-[2px] bg-emerald-500 -translate-y-1/2 transition-all"
            style={{ width: `${progressPct}%` }}
            data-testid="milestones-progress-fill"
          />
          <div className="relative flex justify-between items-center">
            {MILESTONES.map((m, i) => {
              const done = doneFlags[i];
              const current = i === currentIdx && !done;
              const above = i % 2 === 0;   // alternate labels above / below the rail so they don't collide
              const clickable = onSelectStage && m.stageKey && stageByKey[m.stageKey];
              const Label = clickable ? "button" : "div";
              return (
                <div key={m.key} className="relative flex flex-col items-center flex-1" data-testid={`milestone-${m.key}`}>
                  <Label
                    {...(clickable && {
                      type: "button",
                      onClick: () => onSelectStage(m.stageKey),
                      title: stageByKey[m.stageKey].locked ? `${m.label} — waiting for the previous stage` : `Open the ${m.label} stage`,
                    })}
                    className={`absolute ${above ? "-top-11" : "top-8"} text-center w-24 -translate-x-0 ${clickable ? "rounded-sm hover:bg-orange-50 cursor-pointer" : ""}`}
                  >
                    <div className={`text-[11px] font-semibold leading-tight ${
                      current ? "text-[#FF5A00]" : done ? "text-emerald-700" : "text-neutral-400"
                    } ${current ? "animate-pulse" : ""}`}>
                      {m.label}
                    </div>
                    <div className="text-[10px] font-mono-num text-neutral-500 mt-0.5">
                      {done ? formatDate(milestoneDate(m)) : "—"}
                    </div>
                  </Label>
                  <span className={`h-5 w-5 rounded-full flex items-center justify-center border-2 shrink-0 ${
                    done
                      ? "bg-emerald-500 border-emerald-500 text-white"
                      : current
                        ? "bg-white border-[#FF5A00] ring-2 ring-orange-200 animate-pulse shadow-[0_0_0_4px_rgba(255,90,0,0.12)]"
                        : "bg-white border-neutral-300"
                  }`}>
                    {done ? <CheckCircle2 size={11} strokeWidth={3} /> : current ? <span className="h-1.5 w-1.5 rounded-full bg-[#FF5A00]" /> : <Circle size={8} className="text-neutral-300" />}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-2 flex items-center gap-4 text-[10px] text-neutral-500">
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500"/> Completed</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#FF5A00] ring-2 ring-orange-200"/> In progress</span>
          <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full border border-neutral-300 bg-white"/> Pending</span>
        </div>
      </div>
    </section>
  );
}
